import { useCallback, useEffect, useRef, useState } from 'react'
import { installerVerdict } from '@qim/shared'
import { fetchRunLog } from '../api.js'
import { LOG_PAGE } from './run-log.jsx'
import { VerdictHeadline } from './verdict.jsx'
import type { InstallerVerdict } from '@qim/shared'
import type { JSX } from 'react'

/**
 * A run's log while it is still running — the last few lines, refreshing.
 *
 * ## Why this is not `run-log.tsx`
 *
 * That component holds and folds the *whole* log (IM-166), which is right for
 * reading one and a heavier thing than anyone needs to glance at. It is the wrong shape
 * for **watching** one, which is the common case: someone starts an apply and
 * wants to know it is still moving.
 *
 * So this holds `MAX_TAIL` lines and nothing behind them. Peak memory stops
 * depending on how long the run talked — a couple of kilobytes rather than a
 * couple of megabytes — and the newest line is always in view without anyone
 * scrolling to find it.
 *
 * **The trade is real and the UI says so: you cannot scroll back here.** That is
 * what the full log is for, and it is one button away.
 *
 * ## Why scrolling up pauses it
 *
 * Auto-scroll that fights the reader is the single most irritating thing a log
 * view can do — you spot a line, reach for it, and it is gone. Scrolling away
 * from the bottom pauses the follow; coming back resumes it. The state is shown
 * rather than left to be inferred from the text having stopped moving.
 */

/**
 * How many lines are held.
 *
 * Roughly one screen, which is what makes the "you cannot scroll back" trade
 * honest: everything retained is visible. Amit's call, 2026-09-16.
 */
export const MAX_TAIL = 24

/** Distance from the bottom still counted as "at the bottom", in pixels. */
const AT_BOTTOM = 12

/** How often a live run is read again once caught up. */
const POLL_MS = 4000

/**
 * The most pages one catch-up reads before waiting a tick. At `LOG_PAGE` a
 * 62,000-line apply is seven; the bound only stops a stream that never settles
 * from holding the loop.
 */
const MAX_CATCH_UP_PAGES = 20

const format = (line: { readonly at: string; readonly message: string }): string =>
  `${line.at.slice(11, 19)}  ${line.message}`

export const RunTail = ({
  baseUrl,
  tenantId,
  runId,
  live,
  onOpenFullLog,
  onLines,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly runId: string
  /** While true, keep polling. A finished run is read once. */
  readonly live: boolean
  readonly onOpenFullLog: () => void
  /**
   * Every page's messages, in order, as they arrive (IM-165).
   *
   * So the run screen can summarise a live run from the lines this panel is
   * already fetching — a second reader of the same log would double the
   * cross-account traffic of watching a run. This panel still keeps only its
   * tail; whoever listens decides what to hold.
   */
  readonly onLines?: (messages: readonly string[]) => void
}): JSX.Element => {
  const [lines, setLines] = useState<readonly string[]>([])
  const [dropped, setDropped] = useState(false)
  const [note, setNote] = useState<string | undefined>(undefined)
  const [following, setFollowing] = useState(true)
  const [verdict, setVerdict] = useState<InstallerVerdict | undefined>(undefined)

  const cursor = useRef<string | undefined>(undefined)
  const pane = useRef<HTMLPreElement>(null)
  /*
   * `following` is read inside the scroll handler and the paint effect, both of
   * which want the latest value without re-subscribing. A ref alongside the
   * state keeps the render honest and the handlers stable.
   */
  const followingNow = useRef(true)
  // Read through a ref so a new callback does not restart the poll.
  const listener = useRef(onLines)
  useEffect(() => {
    listener.current = onLines
  }, [onLines])

  useEffect(() => {
    let alive = true
    let timer: ReturnType<typeof setTimeout> | undefined

    const keep = (incoming: readonly string[], replace: boolean): void => {
      setLines((held) => {
        const grown = replace ? [...incoming] : [...held, ...incoming]
        if (grown.length > MAX_TAIL) setDropped(true)
        // The whole point: keep the end, discard the rest. Never grows.
        return grown.slice(-MAX_TAIL)
      })
    }

    /*
     * A finished run is read from its **end**, once.
     *
     * It used to be read from its start, like a live one, and a single page of
     * that — so "this is where it stopped" showed the last 24 of the log's
     * *first* 500 lines, which on a real apply is the middle of the plan and
     * nowhere near where it stopped. `fromTail` is the newest page; it replaces
     * whatever a live read held, since a run that has just finished while
     * watched would otherwise show its tail twice. `onLines` is not fed from
     * it: a tail is not the whole log, and the live summary it feeds is only
     * for a run still going.
     */
    const readEnd = async (): Promise<void> => {
      try {
        const page = await fetchRunLog(baseUrl, tenantId, runId, undefined, true)
        if (!alive) return
        /*
         * The installer's verdict on an exit 10 or 11 is the last thing it
         * prints (IM-186), so it is on this page — read from the page, not
         * from the 24 lines kept, which a long reason list can outrun.
         */
        setVerdict(installerVerdict(page.lines.map((line) => line.message)))
        keep(page.lines.map(format), true)
        setNote(page.note)
      } catch {
        // Nothing more to try: the full log is one button away.
      }
    }

    /*
     * A live run is read **forward to where it is now**, then followed.
     *
     * One 500-line page per four seconds meant a panel opened forty minutes into
     * an apply started at its first line and fell further behind the longer the
     * run talked — the "live" tail could be minutes stale. It catches up at the
     * full-log page size until the cursor stops moving (CloudWatch's only
     * reliable end: an empty page with a *new* token can happen mid-stream),
     * and every page still goes to `onLines`, because the live summary needs all
     * of them to know what is in progress.
     */
    const follow = async (): Promise<void> => {
      try {
        for (let n = 0; n < MAX_CATCH_UP_PAGES; n += 1) {
          const sent = cursor.current
          const page = await fetchRunLog(baseUrl, tenantId, runId, sent, false, LOG_PAGE)
          if (!alive) return
          if (page.lines.length > 0) {
            listener.current?.(page.lines.map((line) => line.message))
            keep(page.lines.map(format), false)
          }
          if (page.next !== undefined) cursor.current = page.next
          setNote(page.note)
          if (page.next === undefined || page.next === sent) break
        }
      } catch {
        // A failed read is not a failed run. The record is authoritative and the
        // next tick reads again.
      }
      if (alive) timer = setTimeout(() => void follow(), POLL_MS)
    }

    void (live ? follow() : readEnd())
    /*
     * Registered for a finished run too (IM-87). StrictMode mounts, cleans up
     * and mounts again, and a read nothing could cancel landed twice.
     */
    return () => {
      alive = false
      if (timer !== undefined) clearTimeout(timer)
    }
  }, [baseUrl, tenantId, runId, live])

  // Stick to the bottom only while the reader is there. Layout effect would fire
  // before paint, but the pane's scrollHeight is what we need *after* it.
  useEffect(() => {
    const element = pane.current
    if (element === null || !followingNow.current) return
    element.scrollTop = element.scrollHeight
  }, [lines])

  const onScroll = useCallback(() => {
    const element = pane.current
    if (element === null) return
    const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < AT_BOTTOM
    if (atBottom === followingNow.current) return
    followingNow.current = atBottom
    setFollowing(atBottom)
  }, [])

  if (lines.length === 0) {
    return <p role="status">{note ?? 'Waiting for the first line…'}</p>
  }

  return (
    <div className="tail">
      {verdict === undefined ? null : <VerdictHeadline verdict={verdict} />}
      <p className="tail-bar">
        <span className={live && following ? 'beacon live' : 'beacon'} aria-hidden="true" />
        <span>
          {!live
            ? 'The run has finished — this is where it stopped'
            : following
              ? 'Following — refreshed every 4 seconds'
              : 'Paused while you are scrolled up'}
        </span>
        <span className="tail-count">
          {lines.length === MAX_TAIL ? `last ${String(MAX_TAIL)} lines` : `${lines.length} lines`}
        </span>
      </p>

      <pre
        className="run-log"
        ref={pane}
        onScroll={onScroll}
        tabIndex={0}
        aria-label={`Recent output from run ${runId}`}
      >
        {lines.join('\n')}
      </pre>

      <p className="tail-foot">
        {/* Said plainly rather than implied. Someone who has scrolled to the top
          of this pane is looking at the oldest line it *kept*, not the start of
          the run, and nothing else on screen would tell them. */}
        {dropped
          ? `Only the last ${String(MAX_TAIL)} lines are kept here. `
          : 'Everything so far is shown here. '}
        <button type="button" className="linkish" onClick={onOpenFullLog}>
          Open the full log
        </button>
      </p>
    </div>
  )
}
